import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { EvaluationService } from '@services/core/evaluation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { subProgress, isValidAnswer, regexOf, answerProgress } from '@shared/evaluation-progress.utils';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { normalizeBlankAnswer, normalizeBlankSubs, numberFallback } from './evaluation-answer.helpers';
import { answerPayloadOf, fileIdOf, fileNameOf, firstFileOf, hasEvidenceContent, reviewPayloadOf, uploadErrorText } from './evaluation-evidence.helpers';
import { downloadAndRelease, presentEvidenceModal, requestEvidenceInput } from './evaluation-evidence-dialog.helpers';
import { requestReviewComment, readReviewComment } from './evaluation-evidence-dialog.helpers';
import { canSeeReviewComment, reviewCommentText } from '@shared/evaluation-review.helpers';
import { showEvidenceError, showTextEvidence } from './evaluation-evidence-dialog.helpers';
import { evidenceLabelOf, resolveEvidenceType } from './evaluation-evidence.helpers';
import { targetPayloadOf, uploadFormData } from './evaluation-evidence.helpers';

@Component({
  selector: 'app-evaluation-question-row',
  templateUrl: './evaluation-question-row.component.html',
  styleUrls: ['./evaluation-question-row.component.scss'],
  imports: [FormsModule, NgClass, NgbTooltipModule],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class EvaluationQuestionRowComponent implements OnInit {
  @Input() answer: any;
  @Input() locked = false;
  @Input() reviewMode = false;
  @Input() signed = false;
  @Input() indexLabel = '';
  @Input() missingIds = new Set<string>();
  @Input() completedIds = new Set<string>();
  @Output() changed = new EventEmitter<void>();
  protected expanded = true;
  protected uploading = false;
  protected invalid = false;
  protected subInvalid: Record<string, boolean> = {};
  private evaluationService = inject(EvaluationService);
  private destroyRef = inject(DestroyRef);
  ngOnInit(): void {
    normalizeBlankAnswer(this.answer);
    normalizeBlankSubs(this.answer?.subitems);
  }
  protected dataType(): string { return this.answer?.question?.questionType?.dataType || 'text'; }
  protected isParent(): boolean { return Boolean(this.answer?.subitems?.length); }
  protected subPct(): number { return subProgress(this.answer); }
  protected isRowAnswered(): boolean { return answerProgress(this.answer) > 0; }
  protected rowProgress(): number {
    if (this.isGoalComplete(this.answer?.question)) return 100;
    return answerProgress(this.answer);
  }
  protected isGoalComplete(q: any): boolean { return this.completedIds.has(q?.id); }
  protected isAnswerLocked(): boolean { return this.locked || this.isGoalComplete(this.answer?.question); }
  protected isSubLocked(sub: any): boolean { return this.locked || this.isGoalComplete(sub?.question); }
  protected isTargetLocked(target: any): boolean {
    if (target === this.answer) return this.isAnswerLocked();
    return this.isSubLocked(target);
  }
  protected isSubAnswered(sub: any): boolean { return answerProgress(sub) > 0; }
  protected subProgressValue(sub: any): number {
    if (this.isGoalComplete(sub?.question)) return 100;
    return answerProgress(sub);
  }
  protected hasMissingGoal(qId: string): boolean { return this.missingIds.has(qId); }
  protected isMissingPersonal(q: any): boolean { return q?.goalType === 'personal' && this.hasMissingGoal(q.id); }
  protected regexToUse(): string | null { return numberFallback(this.dataType(), regexOf(this.answer)); }
  protected checkValid(): boolean {
    const ok = isValidAnswer(this.answer.answer, this.regexToUse());
    this.invalid = !ok;
    return ok;
  }
  protected onAnswerBlur(): void {
    if (this.isAnswerLocked()) return;
    if (!this.checkValid()) return;
    this.saveAnswer();
  }
  protected onCommentsBlur(): void {
    if (this.isAnswerLocked()) return;
    this.saveAnswer();
  }
  private saveAnswer(): void { this.persistAnswer(answerPayloadOf(this.answer)); }
  protected canSeeReview(target: any): boolean { return canSeeReviewComment(target, this.reviewMode, this.signed); }
  protected reviewText(target: any): string { return reviewCommentText(target); }
  protected openReviewModal(target: any) {
    requestReviewComment(target).then((result) => this.applyReviewResult(target, result));
  }
  private applyReviewResult(target: any, result: any): void {
    if (!result?.isConfirmed) return;
    target.reviewComment = readReviewComment() ?? target.reviewComment;
    this.saveReview(target);
  }
  private saveReview(target: any): void { this.persistAnswer(reviewPayloadOf(target)); }
  private persistAnswer(payload: any): void {
    this.evaluationService.updateAnswer(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.changed.emit(),
      error: (err) => this.handleSaveError(err),
    });
  }
  private handleSaveError(err: any): void { if (err?.status === 409) this.notifyGoalConflict(); }
  private notifyGoalConflict(): void {
    Swal.fire({ icon: 'info', title: 'Meta cumplida', text: 'La meta anual de esta pregunta ya está cumplida (100%).' });
    this.changed.emit();
  }
  protected onBooleanChange(value: string): void {
    this.answer.answer = value;
    this.onAnswerBlur();
  }
  protected onFileChange(event: Event): void {
    const file = firstFileOf(event);
    if (!file || this.isAnswerLocked()) return;
    this.uploadMainFile(file);
  }
  private uploadMainFile(file: File): void {
    this.uploading = true;
    this.evaluationService.uploadFile(this.answer, uploadFormData(file)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applyMainUpload(res),
      error: (err) => this.failUpload(err),
    });
  }
  private applyMainUpload(res: any): void {
    this.answer.fileId = res.file?.id || res.id;
    this.answer.file = res.file || res;
    this.uploading = false;
    this.saveAnswer();
  }
  protected subRegex(sub: any): string | null { return numberFallback(sub?.question?.questionType?.dataType, regexOf(sub)); }
  protected evidenceType(): string | null { return resolveEvidenceType(this.answer?.question, this.answer?.answer); }
  protected evidenceTypeForSub(sub: any): string | null { return resolveEvidenceType(sub?.question, sub.answer); }
  protected hasEvidence(target: any): boolean { return hasEvidenceContent(target); }
  protected evidenceLabel(kind: string | null): string { return evidenceLabelOf(kind); }
  protected openEvidenceModal(target: any, label: string = this.indexLabel): void {
    if (this.isTargetLocked(target)) return;
    const kind = this.evidenceKindOf(target);
    if (!kind || kind === 'none') return;
    requestEvidenceInput(target, label, kind).then((result) => this.applyEvidenceResult(target, result));
  }
  private evidenceKindOf(target: any): string | null {
    if (target === this.answer) return this.evidenceType();
    return this.evidenceTypeForSub(target);
  }
  private applyEvidenceResult(target: any, result: any): void {
    if (!result?.isConfirmed) return;
    this.storeEvidenceResult(target, result.value || {});
  }
  private storeEvidenceResult(target: any, value: any): void {
    if (value.text !== null) target.comments = value.text;
    if (value.file) this.uploadEvidenceFile(target, value.file);
    else this.saveTarget(target);
  }
  private uploadEvidenceFile(target: any, file: File): void {
    this.evaluationService.uploadFile(target, uploadFormData(file)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applyUploadResult(target, res),
      error: (err) => this.failUpload(err),
    });
  }

  private failUpload(err: any): void {
    this.uploading = false;
    Swal.fire({ icon: 'error', title: 'Error', text: uploadErrorText(err) });
  }
  private applyUploadResult(target: any, res: any): void {
    target.fileId = res.file?.id || res.id;
    target.file = res.file || res;
    this.saveTarget(target);
  }
  protected viewEvidence(target: any): void {
    if (fileIdOf(target)) this.previewEvidenceFile(target);
    else showTextEvidence(target.comments || '');
  }
  private previewEvidenceFile(target: any): void {
    this.evaluationService.getFile(fileIdOf(target)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (blob: Blob) => this.presentEvidencePreview(target, blob),
      error: () => showEvidenceError(target.file),
    });
  }
  private presentEvidencePreview(target: any, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    presentEvidenceModal(target, url, blob.type).then((result) => this.finishEvidencePreview(url, target, result));
  }
  private finishEvidencePreview(url: string, target: any, result: any): void {
    if (result?.isConfirmed) downloadAndRelease(url, fileNameOf(target.file));
    else URL.revokeObjectURL(url);
  }
  private saveTarget(target: any): void { this.persistAnswer(targetPayloadOf(target)); }
  protected subAnswerBlur(sub: any): void {
    if (this.isSubLocked(sub)) return;
    const valid = isValidAnswer(sub.answer, this.subRegex(sub));
    this.subInvalid[sub.id] = !valid;
    if (valid) this.persistAnswer(answerPayloadOf(sub));
  }
  protected onSubFileChange(event: Event, sub: any): void {
    const file = firstFileOf(event);
    if (!file || this.isSubLocked(sub)) return;
    this.uploadSubFile(sub, file);
  }
  private uploadSubFile(sub: any, file: File): void {
    this.evaluationService.uploadFile(sub, uploadFormData(file)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applySubUpload(sub, res),
      error: (err) => this.failUpload(err),
    });
  }
  private applySubUpload(sub: any, res: any): void {
    sub.fileId = res.file?.id || res.id;
    sub.file = res.file || res;
    this.subAnswerBlur(sub);
  }
}
